import React from 'react';
import { AlertCircle } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Card, CardContent } from './ui/card';
import { cn } from '../lib/utils';

export interface FocusShellProps {
  /** The page's h1 (24/600, focusable). */
  title: string;
  description?: React.ReactNode;
  /** The card body. */
  children: React.ReactNode;
  /** Below the column, at the foot of the screen (Login's legal links). */
  footer?: React.ReactNode;
  /** Column width: 'sm' (default) for sign-in, 'xl' for explanatory pages. */
  width?: 'sm' | 'xl';
  className?: string;
}

/**
 * Every full-screen page outside the app chrome: Login, Callback (waiting and
 * error), SetPassword, the ProtectedRoute auth error and NoOrganizationAccess
 * (DESIGN_SYSTEM.md §6 template 5).
 *
 * It owns the backdrop, the fish mark and an on-scale h1. The page supplies
 * the body and keeps the action order: exactly ONE primary button, and any
 * further actions at outline or ghost/link weight — never a second primary.
 *
 * Not for the in-app 404 and 403, which render inside the shell: those are
 * `EmptyState titleAs="h1"`.
 */
export const FocusShell: React.FC<FocusShellProps> = ({
  title,
  description,
  children,
  footer,
  width = 'sm',
  className,
}) => (
  <div
    className={cn(
      'flex min-h-screen flex-col bg-background bg-gradient-to-br from-primary/10 via-background to-background',
      className,
    )}
  >
    <main className="flex flex-1 items-center justify-center p-6">
      <div className={cn('w-full space-y-8', width === 'xl' ? 'max-w-xl' : 'max-w-sm')}>
        <div className="flex flex-col items-center gap-3 text-center">
          <img src="/favicon.svg" alt="Dory" className="h-14 w-14" />
          <div>
            <h1 tabIndex={-1} className="text-2xl font-semibold tracking-tight outline-none">
              {title}
            </h1>
            {description ? <p className="mt-1 text-sm text-muted-foreground">{description}</p> : null}
          </div>
        </div>
        {children}
      </div>
    </main>
    {footer}
  </div>
);

export interface FocusShellFailureProps {
  /** Names what failed ("Sign-in failed"). */
  alertTitle: string;
  /** The cause, already safe to show (describeError's sentence) — never a raw error message. */
  message: React.ReactNode;
  /** The one way forward: a full-width primary button. */
  action: React.ReactNode;
}

/**
 * An auth failure inside a FocusShell: a destructive Alert plus one full-width
 * primary. Callback and ProtectedRoute both render it, so a sign-in failure
 * looks the same whichever of them catches it.
 */
export const FocusShellFailure: React.FC<FocusShellFailureProps> = ({ alertTitle, message, action }) => (
  <Card>
    <CardContent className="space-y-4 py-6">
      <Alert variant="destructive">
        <AlertCircle aria-hidden="true" />
        <AlertTitle>{alertTitle}</AlertTitle>
        <AlertDescription>{message}</AlertDescription>
      </Alert>
      {action}
    </CardContent>
  </Card>
);
